import { Color, Matrix4, Quaternion, Scene, Vector3 } from "three";
import { Brush } from "three-bvh-csg";
import { OBB } from "three/examples/jsm/math/OBB.js";
import { generateUUID } from "three/src/math/MathUtils.js";
import { CameraInterface } from "../engine/Engine";
import { Point } from "./Point";
import { Register } from "../class/ClassRegistry";

const UP = new Vector3(0, 1, 0);
const RIGHT = new Vector3(1, 0, 0);
@Register("Line")
export class Line extends Point {
  protected _startPoint?: Point;
  protected _endPoint?: Point;

  protected _direction?: Vector3;
  protected _normal?: Vector3;
  protected _rotation: Quaternion = new Quaternion();

  protected _length: number = 0;
  protected _thickness: number = 0.14;
  protected _matrix: Matrix4 = new Matrix4();

  constructor(start: Point, end: Point) {
    super();
    this._type = "Line";
    this._name = "Line_" + generateUUID();

    this.start = start;
    this.end = end;
    this.isDirty = true;
  }

  get start() {
    return this._startPoint!;
  }

  set start(value: Point) {
    if (this._startPoint && this._startPoint != this._endPoint)
      this.unbindJoint(this._startPoint);
    this._isDirty = true;
    this._startPoint = value;
    if (this._startPoint) this.bindJoint(this._startPoint);
    this.updateTransform();
  }

  get end() {
    return this._endPoint!;
  }

  set end(value: Point) {
    if (this._endPoint && this._startPoint != this._endPoint)
      this.unbindJoint(this._endPoint);
    this._isDirty = true;
    this._endPoint = value;
    if (this._endPoint) this.bindJoint(this._endPoint);
    this.updateTransform();
  }

  set visibility(value: boolean) {}
  get center() {
    if (!this.start || !this.end) return new Vector3();
    const c = this.start.point.clone();
    c.add(this.end.point);
    return c.divideScalar(2);
  }

  get direction() {
    return this._direction;
  }

  get normal() {
    return this._normal;
  }

  get rotation() {
    return this._rotation;
  }

  get length() {
    return this._length;
  }

  set thickness(value: number) {
    this._thickness = value;
  }

  get thickness() {
    return this._thickness;
  }

  set color2d(color: Color) {
    super.color2d = color;
    this.isDirty = true;
  }

  get color2d() {
    return this._color2d!;
  }

  resetColor() {}

  //private _localOBB?: OBB = new OBB();
  protected _worldOBB?: OBB = new OBB();
  get obb(): OBB | undefined {
    if (this._obbDirty) {
      this.updateObb();
      this._obbDirty = false;
    }
    return this._worldOBB;
  }

  protected _brush?: Brush;
  get brush(): Brush | undefined {
    return this._brush;
  }

  protected _obbDirty = true;
  protected _brushDirty = true;

  set isDirty(value: boolean) {
    if (value) {
      this._obbDirty = value;
      this._brushDirty = value;
    }
    this._isDirty = value;
  }

  get isDirty() {
    return this._isDirty;
  }

  updateTransform() {
    if (!this.end || !this.start) return;

    this._length = this.start.point.distanceTo(this._endPoint!.point);
    if (this.length !== 0) {
      this._direction = this.end.point
        .clone()
        .sub(this.start.point!)
        .normalize();
      this._normal = new Vector3(0, -1, 0).cross(this._direction);
    }

    this._point = this.start.point
      .clone()
      .add(this.end.point)
      .multiplyScalar(0.5);

    if (this.direction && this.normal) {
      this._rotation.setFromUnitVectors(RIGHT, this._direction!);
    }
  }

  protected get scaleObb() {
    return new Vector3(this.length, 1, 1);
  }

  protected updateObb() {
    const d = this.direction;
    const u = UP;
    const n = this.normal;
    const s = this.scaleObb;
    this._worldOBB?.center.copy(this.point);
    this._worldOBB?.halfSize.set(s.x * 0.5, s.y * 0.5, s.z * 0.5);

    if (d && n) {
      this._worldOBB?.rotation.set(
        d?.x,
        d?.y,
        d?.z,
        u.x,
        u.y,
        u.z,
        n?.x,
        n?.y,
        n?.z
      );
    }
  }

  protected _hidden = false;

  get hidden(): boolean {
    return this._hidden;
  }

  set hidden(value: boolean) {
    this._hidden = value;
  }

  get visible(): boolean {
    return this._isVisible;
  }

  set visible(value: boolean) {
    this._isVisible = value;
  }

  dispose(scene?: Scene): void {
    const start = this.start;
    const end = this.end;
    super.dispose();

    if (start.joints?.length == 0) start.dispose();
    if (end.joints?.length == 0) end.dispose();
  }

  angleTo(line: Line): number {
    const v1 = this.direction!.clone().normalize();
    const v2 = line.direction!.clone().normalize();
    return Math.acos(v1.dot(v2));
  }

  update(scene?: Scene, camera?: CameraInterface): void {
    this.updateTransform();
  }

  render(scene?: Scene, camera?: CameraInterface): void {
    this.updateTransform();
    super.render(scene, camera);
  }
}
